import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { DatePicker, Upload } from "antd";
import dayjs from "dayjs";
import { ImageIcon, X } from "lucide-react";
import DateTimeRangePicker from "../../components/FormElements/DateTimeRangePicker";
import CustomTextField from "../../components/FormElements/CustomTextField";
import CustomMultiSelect from "../../components/FormElements/MultiSelect";

const { Dragger } = Upload;

const labelStyles = {
  color: "#828282",
  fontSize: "16px",
  position: "relative",
  top: "7px",
};

const isContestEditable = (contest, isAdmin = false) => {
  if (!contest) return true;
  if (isAdmin) return true;

  const today = dayjs();
  const endDate = dayjs(contest.endDate);
  const votingEndDate = dayjs(contest.votingEndDate);
  const hasPassed =
    endDate.isBefore(today) &&
    votingEndDate.isBefore(today) &&
    !contest.isEnded;

  return !contest.isDeleted && !contest.isEnded && !hasPassed;
};

export default function ContestFormModal({
  classes,
  open,
  selectedItem,
  isAdmin,
  textData,
  onTextChange,
  fileData,
  backgroundFileData,
  onFileUpload,
  onBackgroundFileUpload,
  onRemoveLogo,
  onRemoveBackground,
  submissionStartDate,
  submissionEndDate,
  votingStartDate,
  votingEndDate,
  showListDate,
  votingDurationHours,
  onVotingStartDateChange,
  onVotingEndDateChange,
  onShowListDateChange,
  onVotingDurationChange,
  allAudios,
  selectedAudioIds,
  onSelectedAudioIdsChange,
  onDelete,
  onClose,
  onSubmit,
  showRegionalPrizes,
  regionalPrizeAmount,
  onRegionalPrizeAmountChange,
  regionalSlots,
  onRegionalSlotChange,
  onAddRegionalSlot,
  onRemoveRegionalSlot,
  regionalRegionsText,
  onRegionalRegionsTextChange,
}) {
  return (
    <Dialog
      sx={{
        zIndex: 9,
        "& .MuiDialog-container .MuiPaper-elevation": {
          borderRadius: "8px",
          width: "560px",
        },
      }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          padding: "30px 0 15px",
          textAlign: "center",
          fontWeight: 700,
        }}
      >
        {selectedItem ? "Update" : "Add"} Contest
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {selectedItem && !isContestEditable(selectedItem, isAdmin) && (
          <Box
            sx={{
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              borderRadius: "4px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#d32f2f",
                fontWeight: 500,
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              ⚠️ This contest has passed/ended and can't be edited.
            </Typography>
          </Box>
        )}

        <Box mt={1}>
          <CustomTextField
            label="Title"
            name="title"
            value={textData.title}
            onChange={onTextChange}
            inputProps={{ name: "title", placeholder: "Enter Title" }}
          />
        </Box>
        <Box mt={2} mb={1}>
          <label style={{ ...labelStyles, top: "-9px" }}>Logo</label>
          {fileData?.url || selectedItem?.logo ? (
            <Box className="contest-upload-preview">
              <img src={fileData?.url || selectedItem?.logo} alt="logo" />
              <button
                className="contest-upload-remove"
                onClick={onRemoveLogo}
                type="button"
              >
                <X size={14} />
              </button>
            </Box>
          ) : (
            <Dragger
              showUploadList={false}
              customRequest={onFileUpload}
              accept="image/*"
              className="contest-upload-dragger"
            >
              <Box className="contest-upload-dragger-content">
                <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Drag & drop logo here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to browse (JPG, PNG, max 5MB)
                </Typography>
              </Box>
            </Dragger>
          )}
        </Box>
        <Box mt={2} mb={1}>
          <label style={{ ...labelStyles, top: "-9px" }}>
            Background Image
          </label>
          {backgroundFileData?.url || selectedItem?.backgroundImage ? (
            <Box className="contest-upload-preview contest-upload-preview--wide">
              <img
                src={
                  backgroundFileData?.url || selectedItem?.backgroundImage
                }
                alt="background"
              />
              <button
                className="contest-upload-remove"
                onClick={onRemoveBackground}
                type="button"
              >
                <X size={14} />
              </button>
            </Box>
          ) : (
            <Dragger
              showUploadList={false}
              customRequest={onBackgroundFileUpload}
              accept="image/*"
              className="contest-upload-dragger"
            >
              <Box className="contest-upload-dragger-content">
                <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Drag & drop background image here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to browse (JPG, PNG, max 5MB)
                </Typography>
              </Box>
            </Dragger>
          )}
        </Box>

        {selectedItem ? (
          <>
            <Box>
              <label style={labelStyles}>Submission Time</label>
              <Box
                sx={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Typography variant="body2" sx={{ color: "#666" }}>
                  <strong>Start:</strong>{" "}
                  {dayjs(submissionStartDate).format("YYYY-MM-DD hh:mm A")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                  <strong>End:</strong>{" "}
                  {dayjs(submissionEndDate).format("YYYY-MM-DD hh:mm A")}
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      marginLeft: "8px",
                    }}
                  >
                    (automatically set to 15 minutes before voting starts)
                  </span>
                </Typography>
              </Box>
            </Box>
            <Box>
              <label style={labelStyles}>Voting Time</label>
              <DateTimeRangePicker
                startDate={votingStartDate}
                endDate={votingEndDate}
                onStartDateChange={onVotingStartDateChange}
                onEndDateChange={onVotingEndDateChange}
                disabledDateCustom={(date) =>
                  dayjs(date) <
                  dayjs(new Date(submissionEndDate)).subtract(1, "day")
                }
              />
            </Box>
            <Box display={"block"}>
              <label style={labelStyles}>
                Show on category list date & time
              </label>
              <br />
              <DatePicker
                value={showListDate}
                showTime={{ format: "hh:mm A", use12Hours: true }}
                format="YYYY-MM-DD hh:mm A"
                onChange={onShowListDateChange}
                className="custom-date-time-picker"
                style={{ marginTop: "20px", width: "100%" }}
                placeholder="Select date and time"
              />
              <Typography
                variant="caption"
                sx={{ color: "#999", display: "block", mt: 1 }}
              >
                (Changing this will set the submission start time to 1 minute
                after)
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <label style={labelStyles}>Voting Start Date & Time</label>
              <DatePicker
                value={votingStartDate}
                showTime={{ format: "hh:mm A", use12Hours: true }}
                format="YYYY-MM-DD hh:mm A"
                onChange={onVotingStartDateChange}
                className="custom-date-time-picker"
                style={{ marginTop: "20px", width: "100%" }}
                disabledDate={(date) => dayjs(date) < dayjs().startOf("day")}
                placeholder="Select date and time"
              />
            </Box>
            <Box mt={2}>
              <CustomTextField
                label="Voting Duration (Hours)"
                name="votingDuration"
                type="number"
                value={votingDurationHours}
                onChange={onVotingDurationChange}
                inputProps={{
                  name: "votingDuration",
                  placeholder: "Enter number of hours",
                  min: 1,
                  step: 1,
                }}
              />
            </Box>
            <Box mt={2}>
              <label style={labelStyles}>Calculated Dates</label>
              <Box
                sx={{
                  marginTop: "16px",
                  padding: "12px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#1976d2", mb: 1 }}
                >
                  <strong>Submission Period:</strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px" }}
                >
                  Start: {dayjs().format("YYYY-MM-DD hh:mm A")} (immediately)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px", mt: 0.5 }}
                >
                  End:{" "}
                  {votingStartDate
                    ? dayjs(votingStartDate).format("YYYY-MM-DD hh:mm A")
                    : "Select voting start date"}{" "}
                  (when voting begins)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#1976d2", mt: 2, mb: 1 }}
                >
                  <strong>Voting Period:</strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px" }}
                >
                  Start:{" "}
                  {votingStartDate
                    ? dayjs(votingStartDate).format("YYYY-MM-DD hh:mm A")
                    : "Select date"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px", mt: 0.5 }}
                >
                  End:{" "}
                  {votingStartDate
                    ? dayjs(votingStartDate)
                        .add(votingDurationHours, "hour")
                        .format("YYYY-MM-DD hh:mm A")
                    : "Select date"}{" "}
                  ({votingDurationHours} hour
                  {votingDurationHours !== 1 ? "s" : ""})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#1976d2", mt: 2, mb: 1 }}
                >
                  <strong>Show in Category List:</strong>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "14px" }}
                >
                  {dayjs().format("YYYY-MM-DD hh:mm A")} (immediately)
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Box mt={2}>
          <CustomTextField
            label="Prize"
            name="prize"
            value={textData.prize}
            onChange={onTextChange}
            inputProps={{
              name: "prize",
              placeholder: "Enter Prize",
              maxLength: 100,
            }}
            multiline
          />
        </Box>
        {showRegionalPrizes && (
          <Box mt={2}>
            <Typography variant="body2" sx={{ color: "#142C5C", fontWeight: 700 }}>
              Top in each region
            </Typography>
            <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 0.5 }}>
              People are paired in their region first, then across regions as they advance,
              until defeats leave one worldwide winner. Each win raises the votes the
              next match needs, scaled to how many people entered, so a winner can be
              reached in about an hour. Results are a worldwide ranking and a Top in
              each region. To enter, they have to share their location or type a region.
              Each funded region pays the amount below to its Top. Leave regions blank
              to pay the regions with the most entries. 1 prize unlocks at the first
              entry count, 2 prizes at the next, and so on.
            </Typography>
            <TextField
              label="Prize amount ($)"
              type="number"
              size="small"
              value={regionalPrizeAmount}
              onChange={(event) => onRegionalPrizeAmountChange(event.target.value)}
              inputProps={{ min: 1 }}
              sx={{ mt: 1.5, width: 180 }}
            />
            {regionalSlots.map((slot, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                <TextField
                  label="Prizes"
                  type="number"
                  size="small"
                  value={slot.prizeCount}
                  onChange={(event) =>
                    onRegionalSlotChange(index, "prizeCount", event.target.value)
                  }
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
                <Typography variant="body2" sx={{ color: "#666" }}>
                  × ${regionalPrizeAmount || 0} at
                </Typography>
                <TextField
                  label="Entries"
                  type="number"
                  size="small"
                  value={slot.entriesRequired}
                  onChange={(event) =>
                    onRegionalSlotChange(index, "entriesRequired", event.target.value)
                  }
                  inputProps={{ min: 1 }}
                  sx={{ width: 110 }}
                />
                <IconButton
                  aria-label="Remove prize step"
                  onClick={() => onRemoveRegionalSlot(index)}
                  disabled={regionalSlots.length <= 1}
                  size="small"
                >
                  <X size={16} />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={onAddRegionalSlot} sx={{ mt: 1 }}>
              Add prize step
            </Button>
            <TextField
              label="Prize regions (optional)"
              placeholder="California, Texas"
              size="small"
              fullWidth
              value={regionalRegionsText}
              onChange={(event) => onRegionalRegionsTextChange(event.target.value)}
              helperText="Comma-separated. Blank pays the regions with the most entries."
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        <Box mt={2}>
          <CustomTextField
            label="Details"
            name="details"
            value={textData.details}
            onChange={onTextChange}
            inputProps={{
              name: "details",
              placeholder: "Enter Details",
              maxLength: 5000,
            }}
            multiline
          />
        </Box>

        {selectedItem && (
          <Box mt={2}>
            <Typography
              variant="body2"
              sx={{ color: "#828282", fontSize: "14px", mb: 1 }}
            >
              Assigned Audios
            </Typography>
            {allAudios.length > 0 ? (
              <CustomMultiSelect
                selectOptions={allAudios.map((audio) => ({
                  _id: audio._id,
                  name: audio.name,
                  image: audio.image,
                }))}
                onChange={onSelectedAudioIdsChange}
                placeholder="Select Audios"
                value={selectedAudioIds}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "#999", fontSize: "13px", fontStyle: "italic" }}
              >
                No audios available
              </Typography>
            )}
            {selectedAudioIds.length > 0 && (
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedAudioIds.map((audioId) => {
                  const audio = allAudios.find((item) => item._id === audioId);
                  if (!audio) return null;
                  return (
                    <Chip
                      key={audioId}
                      avatar={
                        <img
                          src={audio.image}
                          alt=""
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                          }}
                        />
                      }
                      label={
                        audio.name?.length > 20
                          ? `${audio.name.slice(0, 20)}...`
                          : audio.name
                      }
                      size="small"
                      onDelete={() =>
                        onSelectedAudioIdsChange(
                          selectedAudioIds.filter((id) => id !== audioId)
                        )
                      }
                      sx={{
                        backgroundColor: "#e3f2fd",
                        "& .MuiChip-label": { fontSize: "12px" },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        <Box
          className={classes.actionsContainer}
          sx={{
            justifyContent: selectedItem && isAdmin ? "space-between" : "flex-end",
          }}
        >
          {selectedItem && isAdmin && (
            <Button
              startIcon={<Delete />}
              sx={{
                border: "1px solid #d32f2f",
                color: "#d32f2f",
                borderRadius: "6px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#ffebee",
                  borderColor: "#b71c1c",
                  color: "#b71c1c",
                },
              }}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          <Box>
            <Button
              sx={{
                border: "1px solid #888",
                color: "#888",
                borderRadius: "6px",
                fontSize: "14px",
                marginRight: "15px",
                "&:hover": {
                  backgroundColor: "#b9b9b9",
                  color: "#222",
                  borderColor: "#b9b9b9",
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className={classes.button}
              onClick={onSubmit}
              disabled={selectedItem ? !isContestEditable(selectedItem) : false}
              sx={{
                ...(selectedItem &&
                  !isContestEditable(selectedItem) && {
                    backgroundColor: "#cccccc !important",
                    color: "#666666 !important",
                    cursor: "not-allowed",
                    "&:hover": {
                      backgroundColor: "#cccccc !important",
                    },
                  }),
              }}
            >
              {selectedItem ? "Update" : "Add"} Contest
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
